<template>
  <div
    :class="{
      'account-open': accountMenuVisible,
    }"
  >
    <div id="page-wrapper" :class="{ desktop: $isDesktop }">
      <!-- Custom header for homepage -->
      <AsyncAppHeaderDesktop v-if="$isDesktop" />
      <AsyncAppHeader
        v-else
        @home-btn-click="clearAndCloseSearchWizard"
        @click="clearAndCloseSearchWizard"
      />

      <!-- Search wizard for homepage -->
      <AsyncSearchWizardHomepageBanner />

      <!-- Main content slot -->
      <slot class="nuxt-content" />

      <!-- Footer section -->
      <template v-if="$isDesktop">
        <AsyncRecentCampsites
          :limit="12"
          :key="getAsyncComponentKey('recentCampsitesDesktop')"
          @vue:mounted="setAsyncComponentKey('recentCampsitesDesktop')"
        />
        <AsyncAppFooterDesktop
          :key="getAsyncComponentKey('appFooterDesktop')"
          :total-counts="totalCounts"
          @vue:mounted="setAsyncComponentKey('appFooterDesktop')"
        />
      </template>
      <template v-else>
        <AsyncRecentCampsites
          :limit="6"
          :key="getAsyncComponentKey('recentCampsitesMobile')"
          @vue:mounted="setAsyncComponentKey('recentCampsitesMobile')"
        />
        <AsyncAppFooter
          :key="getAsyncComponentKey('appFooter')"
          @vue:mounted="setAsyncComponentKey('appFooter')"
        />
      </template>
    </div>

    <AsyncAccountHome
      v-if="accountMenuVisible"
      :key="getAsyncComponentKey('accountHome')"
      @vue:mounted="setAsyncComponentKey('accountHome')"
    />
    <AsyncAppMenu
      v-if="!$isDesktop"
      v-show="appMenuStore.appMenuVisible"
      :app-menu-is-visible="appMenuStore.appMenuVisible"
      :total-counts="totalCounts"
    />
  </div>
</template>

<script setup lang="ts">
import { useNuxtApp, useRoute } from 'nuxt/app'
import { computed, defineAsyncComponent, nextTick, ref, watchEffect } from 'vue'
import { hydrateOnIdle as vueHydrateOnIdle } from 'vue'
import { hydrateOnVisibleSafe } from '../utility/hydration/hydrateOnVisibleSafe'

const AsyncAppHeaderDesktop = defineAsyncComponent({
  loader: () => import('../components/layouts/app-header-desktop.vue'),
  hydrate: vueHydrateOnIdle(10000),
})

const AsyncAppHeader = defineAsyncComponent({
  loader: () => import('../components/layouts/app-header.vue'),
  hydrate: vueHydrateOnIdle(10000),
})

const AsyncSearchWizardHomepageBanner = defineAsyncComponent({
  loader: () =>
    import(
      '../apps/search-wizard/homepage-banner/components/SearchWizardHomepageBanner.vue'
    ),
  hydrate: vueHydrateOnIdle(10000),
})

const pinia = usePinia()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const authStore = useAuthStore(pinia)
const searchWizardStore = useSearchWizardStore(pinia)
const appMenuStore = useAppMenuStore(pinia)
const searchStore = useSearchStoreSetup(pinia)
const { $isDesktop } = useNuxtApp()
const $route = useRoute()

// Async component key management to prevent unnecessary remounts
const asyncComponentKeys = ref<Record<string, string>>({})

const getAsyncComponentKey = (componentName: string) => {
  return (
    asyncComponentKeys.value[componentName] ||
    `${$route.fullPath.split('#')[0]}-${componentName}`
  )
}

const setAsyncComponentKey = (componentName: string) => {
  asyncComponentKeys.value[componentName] =
    `${$route.fullPath.split('#')[0]}-${componentName}`
}

// Add missing async component definitions
const AsyncRecentCampsites = defineAsyncComponent({
  loader: () => import('../components/layouts/components/RecentCampsites.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppFooterDesktop = defineAsyncComponent({
  loader: () => import('../components/layouts/AppFooterDesktop.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppFooter = defineAsyncComponent({
  loader: () => import('../components/layouts/AppFooter.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAccountHome = defineAsyncComponent({
  loader: () => import('../components/layouts/AccountHome.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppMenu = defineAsyncComponent({
  loader: () => import('../components/layouts/components/app-menu/AppMenu.vue'),
  hydrate: vueHydrateOnIdle(10000),
})

// Add computed properties from default layout
const totalCounts = computed(
  () =>
    searchStore.searchCounts?.bookableCount || searchStore.searchCounts?.count,
)

const accountMenuVisible = computed(() => appMenuStore?.accountMenuVisible)

function clearAndCloseSearchWizard() {
  searchWizardStore.isSearchWizardOpen = false
  searchWizardStore.send('CLEAR_SEARCH_PARAMS')
}

// Handle body classes reactively
watchEffect(() => {
  if (process.client) {
    if (searchWizardStore.isSearchWizardOpen && !$isDesktop) {
      document.body.classList.add('homepage-search-open', 'active-tab-search')
      void nextTick(() => {
        document.getElementById('autosuggest__input')?.focus()
      })
    } else if (searchWizardStore.isSearchWizardOpen === false) {
      document.body.classList.remove(
        'homepage-search-open',
        'active-tab-search',
      )
    }
  }
})
</script>

<style scoped>
#page-wrapper {
  align-items: center;
  background-color: white;
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  min-height: 100%;
  height: 100%;
  position: relative;
  transition: transform 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  width: 100vw;
  z-index: 3;
}

.nuxt-content {
  min-height: 100%;
  height: 100%;
}

.account-open :deep(#user) {
  z-index: 2;
}

.account-open.pagemask {
  pointer-events: none;
}

.account-open :deep(#page-wrapper),
[data-menu-open='true'] #page-wrapper {
  position: fixed;
  transform: translateX(0);
  box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.35);
}

[data-menu-open='true'] #page-wrapper {
  transform: translateX(85vw);
}

.account-open :deep(#page-wrapper) {
  transform: translateX(-85vw);
}

.account-open :deep(#page-wrapper),
[data-menu-open='true'] #page-wrapper {
  overflow: hidden;
}

.account-open .nuxt-content,
[data-menu-open='true'] .nuxt-content {
  z-index: -2;
  position: relative;
  overflow-x: visible;
  min-height: 100vh;
}

.account-open :deep(.search-summary-bar),
[data-menu-open='true'] .search-summary-bar {
  z-index: 1;
}

.account-open :deep(.nav-bar .account-icon),
.account-open :deep(#back-icon) {
  display: block;
  background: white;
  margin: 0;
}

.account-open :deep(#account-icon) {
  display: none;
}

.account-open :deep(.nav-label.account) {
  display: none;
}

/* desktop & tablet */
#page-wrapper.desktop {
  width: 100%;
}

[data-menu-open='true'] #page-wrapper.desktop {
  transform: translateX(0);
}

.account-open :deep(#page-wrapper.desktop) {
  transform: translateX(-50vw);
}
</style>
